import Swiper from './swiper.min.js';

/**
 * Theme — Grid Slider  (component-grid-slider.js)
 *
 * Shared Swiper contract for every card-grid section:
 *   featured-products · featured-collection · popular-products
 *   recently-viewed   · product-recommendations · blog cards
 *
 * ──────────────────────────────────────────────────────────────────
 * MARKUP CONTRACT
 * ──────────────────────────────────────────────────────────────────
 *
 *   <grid-slider
 *     data-slider-mobile="true"           ← init Swiper on mobile?
 *     data-slider-desktop="false"         ← init Swiper on desktop?
 *     data-slides-mobile="1.2"            ← slidesPerView on mobile
 *     data-slides-desktop="4"             ← slidesPerView on desktop
 *     data-gap="20"                       ← spaceBetween (px)
 *     data-loop="false"
 *     data-speed="400"
 *     data-transition-profile="slideshow" ← optional: slideshow-like motion
 *     data-progress-indicator="bars"      ← optional: progress line + counter
 *     data-show-nav="true"
 *     data-show-pagination="false"
 *     aria-label="{{ slider_accessible_label }}"
 *   >
 *     <div class="grid-slider__swiper">
 *       <ul class="grid-slider__track" role="list">
 *         <li class="grid-slider__item"><!-- card --></li>
 *       </ul>
 *     </div>
 *
 *     <div class="grid-slider__nav" aria-hidden="true">
 *       <button class="grid-slider__btn grid-slider__btn--prev" type="button" hidden>
 *         {% render 'icon', icon: 'arrow-left' %}
 *         <span class="visually-hidden">{{ 'accessibility.previous_slide' | t }}</span>
 *       </button>
 *       <button class="grid-slider__btn grid-slider__btn--next" type="button" hidden>
 *         {% render 'icon', icon: 'arrow-right' %}
 *         <span class="visually-hidden">{{ 'accessibility.next_slide' | t }}</span>
 *       </button>
 *     </div>
 *
 *     <div class="grid-slider__pagination" aria-hidden="true"></div>
 *     <div class="slider-progress-wrap" aria-hidden="true"></div>
 *   </grid-slider>
 *
 * ──────────────────────────────────────────────────────────────────
 * FALLBACK (no JS / Swiper not active)
 * ──────────────────────────────────────────────────────────────────
 *   component-grid-slider.css provides a CSS grid fallback so content
 *   is readable before JS runs, and at breakpoints where Swiper is off.
 *   Nav buttons start hidden and are revealed only when Swiper inits.
 *
 * ──────────────────────────────────────────────────────────────────
 * SECTION EDITOR SAFETY
 * ──────────────────────────────────────────────────────────────────
 *   Re-init on shopify:section:load fires only when the event target
 *   is inside this element's own shopify-section ancestor.
 */

const DESKTOP_MQ = '(min-width: 700px)';
// Between 700px and 999px the desktop column count is usually too many: a
// 4-across row leaves ~170px cards, and card-collection__info is absolutely
// positioned at the bottom of the media box, so once its content is taller
// than the image it overflows upward and gets clipped. Give tablets their
// own tier. Sections may set data-slides-tablet; otherwise fall back to
// half the desktop count, floor 2.
const TABLET_MQ = '(min-width: 700px) and (max-width: 999px)';

class GridSlider extends HTMLElement {
  constructor() {
    super();
    /** @type {import('./swiper.min.js').default | null} */
    this.swiper      = null;
    this.swiperEl    = null;
    this.prevBtn     = null;
    this.nextBtn     = null;
    this.paginationEl = null;
    this.progressEl  = null;
    this.progressCurrentEl = null;
    this.progressTotalEl = null;
    this.progressFillEl = null;
    this.sectionRoot = null;
    /** @type {MediaQueryList | null} */
    this.mqDesktop   = null;

    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    this._onSectionLoad      = this._onSectionLoad.bind(this);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  connectedCallback() {
    this.swiperEl     = this.querySelector('.grid-slider__swiper');
    this.paginationEl = this.querySelector('.grid-slider__pagination');
    this.progressEl   = this.querySelector('.slider-progress-wrap');
    this.sectionRoot  = this.closest('.shopify-section') || null;

    // External nav: prefer IDs from data-nav-prev / data-nav-next,
    // fall back to buttons found inside this element.
    const prevId = this.dataset.navPrev;
    const nextId = this.dataset.navNext;
    this.prevBtn = prevId
      ? document.getElementById(prevId)
      : this.querySelector('.grid-slider__btn--prev');
    this.nextBtn = nextId
      ? document.getElementById(nextId)
      : this.querySelector('.grid-slider__btn--next');

    // i18n-ready a11y strings — set via data attributes from Liquid | t.
    this._a11yPrev   = this.dataset.a11yPrev   || 'Previous slide';
    this._a11yNext   = this.dataset.a11yNext   || 'Next slide';
    this._a11yBullet = this.dataset.a11yBullet || 'Go to slide {{index}}';

    if (this._showBarProgress && !(this.progressEl instanceof HTMLElement)) {
      this.progressEl = document.createElement('div');
      this.progressEl.className = 'slider-progress-wrap';
      this.progressEl.setAttribute('aria-hidden', 'true');
      this.append(this.progressEl);
    }

    // Do NOT pre-populate progress markup here. _ensureProgressMarkup() runs
    // lazily inside _updateProgressVisual(), which is only called once Swiper
    // is active. Pre-populating here left "01 / 01" content in the DOM while
    // the element was supposed to be hidden — and CSS display:flex overrides
    // the hidden attribute's UA display:none, making it visible.

    if (this.progressEl) this.progressEl.hidden = true;

    this.mqDesktop = window.matchMedia(DESKTOP_MQ);
    this.mqTablet  = window.matchMedia(TABLET_MQ);

    this._syncListSemantics(false);
    this._initBreakpoint();
    this.mqDesktop.addEventListener('change', this._onBreakpointChange);
    this.mqTablet.addEventListener('change', this._onBreakpointChange);
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  disconnectedCallback() {
    this._destroySwiper();
    this.mqDesktop?.removeEventListener('change', this._onBreakpointChange);
    this.mqTablet?.removeEventListener('change', this._onBreakpointChange);
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
  }

  // ─── Event handlers ───────────────────────────────────────────────

  _onBreakpointChange() {
    this._initBreakpoint();
  }

  _onSectionLoad(event) {
    // Only react to section loads that contain this element.
    if (this.sectionRoot && !this.sectionRoot.contains(event.target)) return;
    this._destroySwiper();
    this._initBreakpoint();
  }

  // ─── Data attribute accessors ─────────────────────────────────────

  get _sliderMobile()      { return this.dataset.sliderMobile   === 'true'; }
  get _sliderDesktop()     { return this.dataset.sliderDesktop  === 'true'; }
  get _slidesMobile()      { return parseFloat(this.dataset.slidesMobile)  || 1.2; }
  get _slidesDesktop()     { return parseFloat(this.dataset.slidesDesktop) || 4; }
  get _slidesTablet() {
    const explicit = parseFloat(this.dataset.slidesTablet);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    return Math.max(2, Math.ceil(this._slidesDesktop / 2));
  }
  get _gap()               { return parseInt(this.dataset.gap, 10) || 16; }
  get _peekEnabled()       { return this.dataset.peek === 'true'; }
  get _peekAmount() {
    const amount = parseFloat(this.dataset.peekAmount);
    if (!Number.isFinite(amount)) return 0.14;
    return Math.min(Math.max(amount, 0.05), 0.45);
  }
  get _loop()              { return this.dataset.loop === 'true'; }
  get _speed()             { return parseInt(this.dataset.speed, 10) || 400; }
  get _transitionProfile() { return (this.dataset.transitionProfile || '').trim(); }
  get _progressIndicator() { return (this.dataset.progressIndicator || '').trim(); }
  get _showBarProgress()   { return this._progressIndicator === 'bars'; }
  get _showNav()           { return this.dataset.showNav !== 'false'; }
  get _showPagination()    { return this.dataset.showPagination === 'true'; }

  _resolveTransitionSpeed(prefersReduced) {
    if (prefersReduced) return 0;

    if (this._transitionProfile === 'slideshow') {
      const motionSpeedSetting = (document.body?.dataset.motionSpeed || 'normal').trim();
      switch (motionSpeedSetting) {
        case 'express':
          return 580;
        case 'subtle':
          return 900;
        default:
          return 720;
      }
    }

    return this._speed;
  }

  /**
   * Leading offset before the first slide (px).
   * `data-offset-before="auto"` → reads --page-margin from computed styles.
   * `data-offset-before="24"`   → explicit px value.
   * Omitted / "0"               → no offset.
   */
  get _offsetBefore() {
    const raw = this.dataset.offsetBefore;
    if (!raw || raw === '0') return 0;
    if (raw === 'auto') return this._readPageMarginPx();
    return parseInt(raw, 10) || 0;
  }

  /** Measures --page-margin as pixels using a zero-cost hidden element. */
  _readPageMarginPx() {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:var(--page-margin,1.5rem);visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    const px = probe.offsetWidth;
    probe.remove();
    return px;
  }

  get _slideCount() {
    return this.querySelectorAll('.grid-slider__item').length;
  }

  _syncListSemantics(isSwiperActive) {
    const track = this.querySelector('.grid-slider__track');
    if (!(track instanceof HTMLElement)) return;

    if (!track.hasAttribute('data-original-role')) {
      const originalTrackRole = track.getAttribute('role');
      track.setAttribute('data-original-role', originalTrackRole === null ? '' : originalTrackRole);
    }

    const slides = Array.from(track.querySelectorAll('.grid-slider__item'));
    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      if (slide.hasAttribute('data-original-role')) return;
      const originalSlideRole = slide.getAttribute('role');
      slide.setAttribute('data-original-role', originalSlideRole === null ? '' : originalSlideRole);
    });

    if (isSwiperActive) {
      track.setAttribute('role', 'presentation');
      return;
    }

    const originalTrackRole = track.getAttribute('data-original-role');
    if (originalTrackRole === '') {
      track.removeAttribute('role');
    } else if (originalTrackRole !== null) {
      track.setAttribute('role', originalTrackRole);
    }

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const originalSlideRole = slide.getAttribute('data-original-role');
      if (originalSlideRole === '') {
        slide.removeAttribute('role');
      } else if (originalSlideRole !== null) {
        slide.setAttribute('role', originalSlideRole);
      }
    });
  }

  // ─── Core init / destroy ──────────────────────────────────────────

  /**
   * Called on connectedCallback and breakpoint change.
   * Decides whether Swiper should be running at the current viewport.
   */
  _initBreakpoint() {
    const isDesktop   = this.mqDesktop.matches;
    const isTablet    = this.mqTablet ? this.mqTablet.matches : false;
    const needSlider  = isDesktop ? this._sliderDesktop : this._sliderMobile;
    let baseSlidesPerView = isDesktop ? this._slidesDesktop : this._slidesMobile;
    if (isTablet) baseSlidesPerView = this._slidesTablet;
    const slidesPerView = this._resolveSlidesPerView(baseSlidesPerView);

    if (needSlider && this._slideCount > 1) {
      if (this.swiper) {
        // Already running — update slidesPerView only.
        this.swiper.params.slidesPerView = slidesPerView;
        this.swiper.update();
        this._updateProgressVisual();
      } else {
        this._initSwiper(slidesPerView);
      }
    } else {
      this._destroySwiper();
    }
  }

  _resolveSlidesPerView(baseSlidesPerView) {
    if (!this._peekEnabled) return baseSlidesPerView;

    const rounded = Math.round(baseSlidesPerView);
    const isWhole = Math.abs(baseSlidesPerView - rounded) < 0.001;
    if (!isWhole) return baseSlidesPerView;

    if (baseSlidesPerView <= 1) {
      return baseSlidesPerView + this._peekAmount;
    }

    return baseSlidesPerView + this._peekAmount;
  }

  /**
   * Initialise Swiper with the resolved slidesPerView.
   * @param {number} slidesPerView
   */
  _initSwiper(slidesPerView) {
    if (!this.swiperEl || !Swiper) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transitionSpeed = this._resolveTransitionSpeed(prefersReduced);

    const navConfig =
      this._showNav && this.prevBtn && this.nextBtn
        ? { prevEl: this.prevBtn, nextEl: this.nextBtn }
        : false;

    const paginationConfig =
      this._showPagination && this.paginationEl
        ? { el: this.paginationEl, clickable: true, dynamicBullets: true }
        : false;

    const offsetBefore = this._offsetBefore;

    /** @type {import('./swiper.min.js').SwiperOptions} */
    const options = {
      speed: transitionSpeed,
      loop:  this._loop,
      slidesPerView,
      spaceBetween:       this._gap,
      slidesOffsetBefore: offsetBefore,
      slidesOffsetAfter:  offsetBefore,
      threshold:          8,
      allowTouchMove:     true,
      simulateTouch:      true,
      touchRatio:         1,
      grabCursor:         !prefersReduced,
      watchSlidesProgress: true,

      // Custom class names so markup doesn't need swiper-wrapper / swiper-slide.
      wrapperClass: 'grid-slider__track',
      slideClass:   'grid-slider__item',

      // Accessibility (Swiper built-in).
      a11y: {
        enabled:                 true,
        prevSlideMessage:        this._a11yPrev,
        nextSlideMessage:        this._a11yNext,
        paginationBulletMessage: this._a11yBullet,
        notificationClass:       'visually-hidden',
      },

      navigation: navConfig,
      pagination: paginationConfig,

      on: {
        snapGridLengthChange: (swiper) => GridSlider._dropSliverStop(swiper),
      },
    };

    this.swiperEl.classList.add('is-ready');
    this.swiper = new Swiper(this.swiperEl, options);
    this.classList.add('is-swiper-active');
    this._syncListSemantics(true);

    // Reveal nav only after Swiper confirms it can scroll.
    if (this.prevBtn) this.prevBtn.hidden = false;
    if (this.nextBtn) this.nextBtn.hidden = false;

    this._setupProgress();
  }

  /**
   * Tear down the Swiper instance and return to CSS fallback state.
   */
  _destroySwiper() {
    // Hide progress unconditionally — must run even when Swiper was never
    // started (e.g. carousel disabled at current breakpoint). Previously this
    // was below the early-return guard, so a null-swiper path skipped it.
    if (this.progressEl) this.progressEl.hidden = true;

    if (!this.swiper) {
      this._syncListSemantics(false);
      return;
    }

    this.swiper.destroy(true, true);
    this.swiper = null;

    // Remove Swiper's own init class so CSS fallback grid re-activates.
    this.swiperEl?.classList.remove('is-ready', 'swiper-initialized');
    this.classList.remove('is-swiper-active');
    this._syncListSemantics(false);

    // Hide navigation in static grid mode.
    if (this.prevBtn) this.prevBtn.hidden = true;
    if (this.nextBtn) this.nextBtn.hidden = true;
  }

  /**
   * With a peek (e.g. 4.14 per view) the last stop sits only the peek's
   * width past the one before it: at 1440px, 37px on to show the final
   * card's edge, yet counted as a page of its own. Drop the stop before the
   * end when the step to the end is under half a card, so the slider goes
   * straight to the true end and the last card still shows in full.
   * Runs whenever Swiper rebuilds its snap grid (init, update, resize).
   * @param {import('./swiper.min.js').default} swiper
   */
  static _dropSliverStop(swiper) {
    const grid = swiper.snapGrid;
    if (!Array.isArray(grid) || grid.length < 3) return;
    const slide = swiper.slidesSizesGrid?.[0] || 0;
    const lastStep = grid[grid.length - 1] - grid[grid.length - 2];
    if (slide > 0 && lastStep < slide * 0.5) grid.splice(grid.length - 2, 1);
  }

  _setupProgress() {
    if (!this._showBarProgress || !(this.progressEl instanceof HTMLElement) || !this.swiper) return;

    const update = () => this._updateProgressVisual();
    this.swiper.on('slideChange', update);
    this.swiper.on('snapIndexChange', update);
    this.swiper.on('resize', update);
    this.swiper.on('update', update);
    this._updateProgressVisual();
  }

  _ensureProgressMarkup() {
    if (!(this.progressEl instanceof HTMLElement)) return;

    this.progressCurrentEl = this.progressEl.querySelector('.slider-progress-current');
    this.progressTotalEl = this.progressEl.querySelector('.slider-progress-total');
    this.progressFillEl = this.progressEl.querySelector('.slider-progress-fill');

    if (this.progressCurrentEl && this.progressTotalEl && this.progressFillEl) return;

    this.progressEl.innerHTML = `
      <div class="slider-progress-top">
        <span class="slider-progress-current">01</span>
        <span class="slider-progress-divider">/</span>
        <span class="slider-progress-total">01</span>
      </div>
      <div class="slider-progress-track">
        <div class="slider-progress-fill"></div>
      </div>
    `;

    this.progressCurrentEl = this.progressEl.querySelector('.slider-progress-current');
    this.progressTotalEl = this.progressEl.querySelector('.slider-progress-total');
    this.progressFillEl = this.progressEl.querySelector('.slider-progress-fill');
  }

  _formatProgressNumber(value) {
    return String(Math.max(value, 0)).padStart(2, '0');
  }

  _updateProgressVisual() {
    if (!this._showBarProgress || !(this.progressEl instanceof HTMLElement) || !this.swiper) return;

    this._ensureProgressMarkup();

    const stopCount = Math.max(this.swiper.snapGrid?.length || 0, 1);
    if (stopCount <= 1) {
      this.progressEl.hidden = true;
      return;
    }

    const maxIndex = stopCount - 1;
    const activeIndex = Math.min(Math.max(this.swiper.snapIndex || 0, 0), maxIndex);

    const currentSlide = activeIndex + 1;
    const percentage = (currentSlide / stopCount) * 100;

    if (this.progressCurrentEl) {
      this.progressCurrentEl.textContent = this._formatProgressNumber(currentSlide);
    }

    if (this.progressTotalEl) {
      this.progressTotalEl.textContent = this._formatProgressNumber(stopCount);
    }

    if (this.progressFillEl) {
      this.progressFillEl.style.width = `${percentage}%`;
    }

    this.progressEl.hidden = false;
  }
}

if (!customElements.get('grid-slider')) {
  customElements.define('grid-slider', GridSlider);
}
